import { Actor, PointerType } from "../action/Actor.js";
import { Caps } from "../core/Caps.js";
import { Player } from "../core/Player.js";
import { Signal } from "../core/Signal.js";
import { Calc } from "../math/Calc.js";
import { IListData, IListProvider, IListVisual, ScrollOptions } from "./List.js";
import { ScrollbarTheme } from "./Scrollbar.js";
import { ScrollbarListDynamic } from "./ScrollbarListDynamic.js";

export interface IListDynamicProvider<Data extends IListData, Visual extends IListVisual = IListVisual> extends IListProvider<Data, Visual> {
    dispose?(data: Data, visual: Visual): void;
}

export class ListItem<Data extends IListData, Visual extends IListVisual>{
    public index: number;
    public height: number = 0;
    public offsetTop: number = 0;
    public element: HTMLDivElement = null;
    public visual: Visual = null;
    public visible: boolean = false;
    public hasValidSize: boolean = false;
    // public recycle: boolean = false;

    private _y: number = -1;

    constructor(
        public data: Data,
        private _provider: IListDynamicProvider<Data, Visual>,
        private _resizeObserver: ResizeObserver = null) {
    }

    public calcHeight(): number {
        // return item.element.offsetHeight;
        return Math.floor(this.element.getBoundingClientRect().height);
    }

    public create(): void {
        if (this.element) {
            console.warn("Could not create ListItem. Element already created.");
            return;
        }
        this.element = document.createElement("div");
        this.element.classList.add("e5-list-dynamic-item");
        this.visual = <IListVisual>{ element: this.element } as any;
        if (this._resizeObserver)
            this._resizeObserver.observe(this.element);
        this._provider.prepare(this.visual);
        this._provider.update(this.data, this.visual);
    }

    public remove(dispose: boolean = false): void {
        if (!this.element) {
            console.warn("Could not remove ListItem. No element defined.");
            return;
        }

        this.element.remove();

        if (dispose) {
            if (typeof this._provider.dispose === "function")
                this._provider.dispose(this.data, this.visual);
            if (this._resizeObserver)
                this._resizeObserver.unobserve(this.element);
            this.element = null;
            this.visual = null;
            this._y = -1;
        }

        this.visible = false;
    }

    public get y(): number {
        return this._y;
    }

    public set y(y: number) {
        if (this._y === y)
            return;
        this._y;
        this.element.style.transform = "translateY(" + y + "px)";
    }

    public dispose(): void {
        if (this.element)
            this.remove();
        this._provider = null;
        this._resizeObserver = null;
        this.data = null;
    }
}

export class ListDynamic<Data extends IListData = IListData, Visual extends IListVisual = IListVisual> {

    public onUpdate = Signal.create();

    public readonly element: HTMLElement;
    public readonly scroller: HTMLElement;
    public readonly content: HTMLDivElement;
    public readonly scrollbar: ScrollbarListDynamic<Data>;

    private _actor: Actor;
    private _provider: IListDynamicProvider<Data, Visual> = null;
    private _resize: (e: any) => void;
    private _visible: boolean = true;
    private _resizeObserver: ResizeObserver = null;
    private _velocityY: number = 0;
    private _maxVelocity: number = 600;
    private _updating: boolean = false;
    private _items: ListItem<Data, Visual>[] = [];
    private _itemsFiltered: ListItem<Data, Visual>[] = [];
    private _itemsActive: ListItem<Data, Visual>[] = [];
    private _lastScroll: number = 0;
    private _contentHeight: number = 0;

    constructor(
        private estimatedHeight: number,
        element: HTMLElement = null,
        autoResize: boolean = true,
        theme: ScrollbarTheme = null) {

        this.element = element || document.createElement("div");
        this.element.classList.add("e5-list-dynamic");

        this.scroller = document.createElement("div");
        this.scroller.classList.add("e5-list-dynamic-scroller");
        this.element.appendChild(this.scroller);

        this.content = document.createElement("div");
        this.content.classList.add("e5-list-dynamic-content");
        this.scroller.appendChild(this.content);

        this._resize = () => this.resize();
        if (autoResize)
            window.addEventListener("resize", this._resize);

        // this._resizeObserver = new ResizeObserver((entries) => this.onResizeItems(entries));

        this.scroller.addEventListener("wheel", (e) => {
            if (this._actor.pointers.length > 0)
                return;
            var speed = 50;
            var delta = speed;
            this._velocityY = 0;
            if (e.deltaY < 0)
                delta = -speed;
            this.scrollTop += delta;
        }, Caps.passiveEventLister ? {
            passive: true
        } : false);

        this._actor = new Actor(this.scroller, {
            passive: false,
            capture: false,
            preventDefault: false,
            filter: PointerType.MOUSE
        });

        var pointer = this._actor.pointer;

        var enabled: boolean = false;
        var minDistance = 15;
        var moveY = 0;
        this._actor.onMove.add(() => {
            if (!enabled) {
                if (Math.abs(pointer.deltaY) > Math.abs(pointer.deltaX)) {
                    moveY += Math.abs(pointer.deltaY);
                }
                else {
                    moveY = 0;
                }
                if (moveY > minDistance)
                    enabled = true;
            }

            if (enabled)
                this.scrollTop -= pointer.deltaY;
        });

        this._actor.onClear.add(() => {
            moveY = 0;
            if (enabled)
                this._velocityY = Calc.clamp(this._velocityY + pointer.velocityY * 30, -this._maxVelocity, this._maxVelocity);
            enabled = false;
        });

        if (theme === null) {
            if (Caps.isMobile) {
                theme = "mobile";
            }
            else if (Caps.os === "windows") {
                theme = "windows";
            } else if (Caps.os === "mac") {
                theme = "mac";
            }
            else {
                //TODO: check for other os's
                theme = "windows";
            }
        }

        this.scrollbar = new ScrollbarListDynamic<Data>(this, theme);

        this.element.appendChild(this.scrollbar.element);

        this.scroller.classList.add("theme-" + theme);
        // scrollbar.update();

        Player.onTick.add(this.applyVelocity, this);
    }

    private applyVelocity(): void {
        if (this._velocityY === 0)
            return;
        this._velocityY *= 0.93;
        if (this._actor.pointers.length > 0) {
            return;
        }
        else {
            this.scrollTop -= Math.round(this._velocityY);
        }
        if (Math.abs(this._velocityY) < 1) {
            this._velocityY = 0;
        }
    }

    public resize(): void {
        this._lastScroll = this.scrollTop;
        var items = this.getItems();
        for (var item of items) {
            item.hasValidSize = false;
        }
        this.update();
    }

    private onResizeItems(entries: ResizeObserverEntry[]): void {
        var isDirty = false;
        for (var entry of entries) {
            if (!entry.target.parentElement)
                continue;

            var item = this.getListItemByElement(entry.target);
            if (item && item.visible) {
                var height = item.calcHeight();
                if (height != item.height) {
                    // item.height = height;
                    // item.hasValidSize = true;
                    isDirty = true;
                    break;
                }
            }
        }

        if (isDirty) {
            this.update();
        }
    }

    public getListItemByElement(element: Element): ListItem<Data, Visual> {
        for (var item of this._items) {
            if (item.element === element)
                return item;
        }
        return null;
    }

    private getListItemByData(data: Data): ListItem<Data, Visual> {
        for (var item of this._items) {
            if (item.data === data)
                return item;
        }
        return null;
    }

    public get visible(): boolean {
        return this._visible;
    }

    public set visible(visible: boolean) {
        if (this._visible === visible)
            return;
        this._visible = visible;
        this.content.classList.toggle("hidden", !visible);
        if (visible)
            this.update();
    }

    public scrollToPosition(scrollTop: number): void {
        if (scrollTop < this.content.offsetTop) {
            this.scrollTop = scrollTop;
        }
        else if (scrollTop > this.content.offsetTop + this.content.clientHeight) {
            this.scrollTop = scrollTop;
        }
        else {
            //TODO: do something good :)
            this.update(scrollTop);
            this.scrollTop = scrollTop;
        }
    }

    public scrollToTop(): void {
        this.update(0);
        this.scrollTop = 0;
    }

    public scrollToBottom(): void {
        var items = this.getItems();
        if (items.length > 0) {
            this.scrollToIndex(items.length - 1);
            // this.update(items[items.length - 1].offsetTop);
        }
        this.scrollTop = this.scroller.scrollHeight - this.scroller.clientHeight;
    }

    public scrollToData(data: Data, options: ScrollOptions = {}): void {
        var item = this.getListItemByData(data);
        if (!item)
            return;
        this.scrollToIndex(item.index, options);
    }

    public scrollToIndex(index: number, options: ScrollOptions = {}): void {
        var items = this.getItems();
        index = Calc.clamp(index, 0, items.length - 1);
        var item = items[index];
        if (!item || !item.data.visible)
            return;

        // console.log("SCROLL INDEX", index);

        var ifNeeded = options.ifNeeded === undefined ? false : options.ifNeeded;
        var marginTop = options.marginTop === undefined ? 0 : options.marginTop;
        var marginBottom = options.marginBottom === undefined ? 0 : options.marginBottom;

        var offsetTop = item.offsetTop + marginTop + this.content.offsetTop - this.scrollTop;
        var offsetBottom = this.scroller.clientHeight - (marginBottom + item.offsetTop + item.height + this.content.offsetTop - this.scrollTop);
        if (ifNeeded && offsetTop > 0 && offsetBottom > 0) {
            return;
        }

        var align = options.align || "top";
        if (align === "auto") {
            if (offsetTop < 0)
                align = "top";
            else
                align = "bottom";
        }

        // this.update(this.clampScrollTop(item.offsetTop + this.content.offsetTop));
        var offsetTopBefore = item.offsetTop + this.content.offsetTop;
        this.update(offsetTopBefore);

        var marginTop = options.marginTop === undefined ? 0 : options.marginTop;
        var marginBottom = options.marginBottom === undefined ? 0 : options.marginBottom;

        var scrollTop: number = this.scrollTop;
        if (align === "top") {
            scrollTop = item.offsetTop + this.content.offsetTop - marginTop;
        }
        else if (align === "bottom") {
            scrollTop = item.offsetTop + this.content.offsetTop;
            scrollTop -= this.scroller.clientHeight - item.height - marginBottom;
        }

        this.scrollTop = scrollTop;

        //TODO: check if this is really needed... TODO TODO TODO...
        // if(offsetTopBefore != this.scrollTop)
        this.update();
    }

    public refresh(dispose: boolean = false): void {
        if (!this.scroller || !this.provider || !this.visible)
            return;

        this._actor.cancel();
        this._velocityY = 0;

        this._itemsFiltered = [];
        var index = 0;
        for (var item of this._items) {
            // if (item.visible && item.element) {
            //     item.remove(dispose);
            // }
            if (item.element) {
                item.remove(dispose);
            }
            if (item.data.visible) {
                item.index = index++;
                this._itemsFiltered.push(item);
            }
        }

        this.update(0);
        this.scrollTop = 0;
    }

    public updateData(data: Data): void {
        if (!data)
            return;
        for (var item of this._items) {
            if (item.data === data) {
                this._provider.update(item.data, item.visual);
            }
        }
    }

    public getItems(): ListItem<Data, Visual>[] {
        var offsetTop = 0;
        for (var item of this._itemsFiltered) {
            item.offsetTop = offsetTop;
            offsetTop += item.height;
        }
        return this._itemsFiltered;
    }

    public update(scrollTop: number = this.scrollTop): void {
        scrollTop = Math.round(scrollTop);
        if (!this._visible || !this.scroller || !this._provider) {
            this.contentHeight = 0;
            return;
        }

        this._updating = true;

        var items = this.getItems();

        var contentOffsetTop = this.content.offsetTop;
        var scrollerClientHeight = this.scroller.clientHeight;
        var scrollOffset = 0;

        var pointerOffset = 0;

        //TODO: should the current pointer position change the "offset-line"?
        // if (this._actor.pointers.length > 0) {
        //     pointerOffset = this._actor.pointer.y - this.element.getBoundingClientRect().top;
        // }
        // else {
        //     var deltaScroll = scrollTop - this._lastScroll;
        //     this._lastScroll = scrollTop;
        //     if (deltaScroll < 0)
        //         pointerOffset = scrollerClientHeight;
        //     else
        //         pointerOffset = 0;
        // }

        //------------
        //Correct heights of invalid-size items
        //------------
        // var estimatedHeight = 0;
        // var validCount = 0;
        // for (var item of items) {
        //     if (item.hasValidSize) {
        //         validCount++;
        //         estimatedHeight += item.height
        //     }
        // }
        // estimatedHeight = validCount > 0 ? Math.floor(estimatedHeight / validCount) : this.estimatedHeight;
        // this.estimatedHeight = estimatedHeight;
        // var h = 0;
        // for (var item of items) {
        //     if (!item.hasValidSize) {
        //         if (estimatedHeight !== item.height) {
        //             var heightDiff = estimatedHeight - item.height;
        //             this.updateOffset(heightDiff, item.index + 1);
        //             if (item.offsetTop + contentOffsetTop < (scrollTop + pointerOffset + scrollOffset))
        //                 scrollOffset += heightDiff;
        //             item.height = estimatedHeight;
        //         }
        //     }
        //     h += item.height;
        // }

        //Get first index
        var firstIndex = 0;
        //Why subtracting scrollOffset ??? Adding should be correct!!!
        var scrollTopOffset = (scrollTop + scrollOffset) - contentOffsetTop;
        for (var item of items) {
            if (item.offsetTop <= scrollTopOffset)
                firstIndex = item.index;
        }
        firstIndex = Calc.clamp(firstIndex, 0, items.length - 1);

        this._itemsActive = [];
        var maxIndex = items.length - 1;
        for (var dataIndex = firstIndex; dataIndex <= maxIndex; ++dataIndex) {
            if (!items[dataIndex])
                continue;

            var maxOffsetTop = scrollTop + scrollerClientHeight + scrollOffset;
            var item = items[dataIndex];
            if (item.offsetTop + contentOffsetTop > maxOffsetTop) {
                if (item.element)
                    item.remove();
                continue;
            }

            if (!item.visible) {
                if (!item.element) {
                    item.create();
                }
                this.content.appendChild(item.element);
            }

            var elementHeight = item.calcHeight();
            if (elementHeight !== item.height) {
                var heightDiff = elementHeight - item.height;
                this.updateOffset(heightDiff, dataIndex + 1);
                if (item.offsetTop + contentOffsetTop < (scrollTop + pointerOffset + scrollOffset))
                    scrollOffset += heightDiff;
                item.height = elementHeight;
                item.hasValidSize = true;
            }

            item.visible = true;
            item.y = item.offsetTop;

            if (!item.element.parentElement)
                this.content.appendChild(item.element);

            this._itemsActive.push(item);
        }

        //Calc content-height and remove invisible items
        var height = 0;
        for (var item of items) {
            if (item.visible && this._itemsActive.indexOf(item) === -1) {
                item.remove();
            }
            item.offsetTop = height;
            height += item.height;
        }
        this.contentHeight = height;

        //Correct scroll position
        if (scrollOffset != 0) {
            this.scrollTop += scrollOffset;
        }

        this.onUpdate.dispatch();
        this._updating = false;
    }

    private get contentHeight(): number {
        return this._contentHeight;
    }

    private set contentHeight(height: number) {
        if (this._contentHeight === height)
            return;
        this._contentHeight = height;
        this.content.style.height = height + "px";
    }

    public get maxItemHeight(): number {
        var maxHeight = 0;
        for (var item of this._itemsFiltered) {
            maxHeight = Math.max(maxHeight, item.height);
        }
        return maxHeight;
    }

    public get minItemHeight(): number {
        var minHeight = Number.MAX_VALUE;
        for (var item of this._itemsFiltered) {
            minHeight = Math.min(minHeight, item.height);
        }
        return minHeight;
    }

    public get first(): ListItem<Data, Visual> {
        return this._itemsActive[0];
    }

    public get last(): ListItem<Data, Visual> {
        return this._itemsActive[this._itemsActive.length - 1];
    }

    private updateOffset(offset: number, startIndex: number): void {
        var items = this.getItems();
        for (var i = startIndex; i < items.length; ++i) {
            items[i].offsetTop += offset;
        }
    }

    public get scrollTop(): number {
        return this.scroller.scrollTop;
    }

    public set scrollTop(value: number) {
        value = this.clampScrollTop(value);
        if (this.scroller.scrollTop === value)
            return;
        this.scroller.scrollTop = value;
        if (!this._updating)
            this.update();
    }

    private clampScrollTop(scrollTop: number): number {
        return Calc.clamp(Math.round(scrollTop), this.minScroll, this.maxScroll);
    }

    private get minScroll(): number {
        return 0;
    }

    private get maxScroll(): number {
        return Math.max(0, this.scroller.scrollHeight - this.scroller.clientHeight);
    }

    public get provider(): IListDynamicProvider<Data, Visual> {
        return this._provider;
    }

    public set provider(provider: IListDynamicProvider<Data, Visual>) {
        if (this._provider === provider)
            return;
        this._provider = provider;

        for (var item of this._items) {
            item.dispose();
        }
        this._items = [];
        for (var listItem of this._provider.dataList) {
            var item = new ListItem<Data, Visual>(listItem, this._provider, this._resizeObserver);
            item.height = this.estimatedHeight;
            this._items.push(item)
        }
        this.refresh();
    }
}